import React, { useState, useEffect } from "react";
import { Tab, Message, Button, Grid, Progress, Header, Loader, Label, Icon } from "semantic-ui-react";
import "./Dashboard.css";
import DijonCard from "./DijonCard";
import DijonMarkets from "./DijonMarkets";
import axiosInstance from "../../lib/auth/axiosApi";

function DijonGipirt(props) {
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [trendingWords, setTrendingWords] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTrendingWords(),
        loadSentiment()
      ]);
    } catch (err) {
      console.error("Error loading GIPIRT data:", err);
      setError("Failed to load trending data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadTrendingWords = async () => {
    try {
      const response = await axiosInstance.get("/gipirt/trending_words/?limit=50");
      setTrendingWords(response.data.trending_words || []);
      setLastUpdated(response.data.last_updated);
    } catch (err) {
      console.error("Error loading trending words:", err);
      throw err;
    }
  };

  const loadSentiment = async () => {
    try {
      const response = await axiosInstance.get("/gipirt/sentiment_meter/?region=global");
      setSentiment(response.data);
    } catch (err) {
      console.error("Error loading sentiment:", err);
      throw err;
    }
  };

  const collectNewData = async () => {
    setLoading(true);
    try {
      await axiosInstance.post("/gipirt/collect_trending_data/");
      await loadData();
    } catch (err) {
      console.error("Error collecting new data:", err);
      setError("Failed to collect new data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (score) => {
    if (score > 0.2) return 'green';
    if (score < -0.2) return 'red';
    return 'yellow';
  };

  const getSentimentLabel = (score) => {
    if (score > 0.2) return 'Positive';
    if (score < -0.2) return 'Negative';
    return 'Neutral';
  };

  const getWordsBySource = (source) => {
    return trendingWords.filter(word => word.source === source);
  };

  const panes = [
    {
      menuItem: 'Trending Words',
      render: () => (
        <Tab.Pane>
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px', textAlign: 'right' }}>
              <Button 
                primary 
                onClick={collectNewData}
                loading={loading}
                disabled={loading}
              >
                <Icon name="refresh" />
                Refresh Data
              </Button>
            </div>
            
            {error && (
              <Message negative onDismiss={() => setError(null)}>
                {error}
              </Message>
            )}

            <Grid stackable columns={3}>
              <Grid.Column>
                <Header as="h3" color="orange">
                  <Icon name="reddit" />
                  Reddit Trends
                </Header>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {getWordsBySource('reddit').map((word, index) => (
                    <div key={index} style={{ 
                      margin: '8px 0', 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{word.word}</strong>
                        <Label color={getSentimentColor(word.sentiment_score)} size="mini">
                          {word.frequency}
                        </Label>
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>
                        Sentiment: {getSentimentLabel(word.sentiment_score)}
                      </div>
                    </div>
                  ))}
                </div>
              </Grid.Column>

              <Grid.Column>
                <Header as="h3" color="blue">
                  <Icon name="line graph" />
                  Google Trends
                </Header>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {getWordsBySource('google_trends').map((word, index) => (
                    <div key={index} style={{ 
                      margin: '8px 0', 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{word.word}</strong>
                        <Label color={getSentimentColor(word.sentiment_score)} size="mini">
                          {word.frequency}
                        </Label>
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>
                        Sentiment: {getSentimentLabel(word.sentiment_score)}
                      </div>
                    </div>
                  ))}
                </div>
              </Grid.Column>

              <Grid.Column>
                <Header as="h3" color="purple">
                  <Icon name="newspaper" />
                  News Trends
                </Header>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {getWordsBySource('news').map((word, index) => (
                    <div key={index} style={{ 
                      margin: '8px 0', 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{word.word}</strong>
                        <Label color={getSentimentColor(word.sentiment_score)} size="mini">
                          {word.frequency}
                        </Label>
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>
                        Sentiment: {getSentimentLabel(word.sentiment_score)}
                      </div>
                    </div>
                  ))}
                </div>
              </Grid.Column>
            </Grid>

            {lastUpdated && (
              <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '0.9em', color: '#666' }}>
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </div>
            )}
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Sentiment Meter',
      render: () => (
        <Tab.Pane>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            {sentiment ? (
              <>
                <Header as="h2" style={{ marginBottom: '30px' }}>
                  Global Sentiment Analysis
                </Header>
                
                <div style={{ marginBottom: '40px' }}>
                  <Header as="h3" color={getSentimentColor(sentiment.sentiment_score)}>
                    Overall Sentiment: {getSentimentLabel(sentiment.sentiment_score)}
                  </Header>
                  <div style={{ fontSize: '2em', fontWeight: 'bold', color: getSentimentColor(sentiment.sentiment_score) === 'green' ? '#21ba45' : getSentimentColor(sentiment.sentiment_score) === 'red' ? '#db2828' : '#fbbd08' }}>
                    {sentiment.sentiment_score > 0 ? '+' : ''}{parseFloat(sentiment.sentiment_score).toFixed(2)}
                  </div>
                </div>

                <Grid columns={3} divided style={{ marginTop: '30px' }}>
                  <Grid.Column textAlign="center">
                    <Header as="h4" color="green">
                      <Icon name="thumbs up" />
                      Positive
                    </Header>
                    <Progress 
                      percent={sentiment.positive_percentage} 
                      color="green" 
                      size="large"
                      style={{ marginBottom: '10px' }}
                    />
                    <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                      {parseFloat(sentiment.positive_percentage).toFixed(1)}%
                    </div>
                  </Grid.Column>

                  <Grid.Column textAlign="center">
                    <Header as="h4" color="yellow">
                      <Icon name="meh" />
                      Neutral
                    </Header>
                    <Progress 
                      percent={sentiment.neutral_percentage} 
                      color="yellow" 
                      size="large"
                      style={{ marginBottom: '10px' }}
                    />
                    <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                      {parseFloat(sentiment.neutral_percentage).toFixed(1)}%
                    </div>
                  </Grid.Column>

                  <Grid.Column textAlign="center">
                    <Header as="h4" color="red">
                      <Icon name="thumbs down" />
                      Negative
                    </Header>
                    <Progress 
                      percent={sentiment.negative_percentage} 
                      color="red" 
                      size="large"
                      style={{ marginBottom: '10px' }}
                    />
                    <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                      {parseFloat(sentiment.negative_percentage).toFixed(1)}%
                    </div>
                  </Grid.Column>
                </Grid>

                <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                  <Header as="h5">Data Sources</Header>
                  <p>Analysis based on trending words from Reddit, Google Trends, and major news sources.</p>
                  {sentiment.data_sources && sentiment.data_sources.total_words_analyzed && (
                    <p>Total words analyzed: {sentiment.data_sources.total_words_analyzed}</p>
                  )}
                </div>
              </>
            ) : (
              <div>
                <Header as="h3">No sentiment data available</Header>
                <p>Click "Refresh Data" to collect current sentiment information.</p>
              </div>
            )}
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Popular Words',
      render: () => (
        <Tab.Pane>
          <div style={{ padding: '20px' }}>
            <Header as="h3" style={{ marginBottom: '20px' }}>
              Most Popular Words Right Now
            </Header>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {trendingWords
                .sort((a, b) => b.frequency - a.frequency)
                .slice(0, 50)
                .map((word, index) => {
                  const size = Math.max(1, Math.min(3, word.frequency / 500));
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: getSentimentColor(word.sentiment_score) === 'green' ? '#e8f5e8' : 
                                       getSentimentColor(word.sentiment_score) === 'red' ? '#fce8e8' : '#fff9e6',
                        border: `2px solid ${getSentimentColor(word.sentiment_score) === 'green' ? '#21ba45' : 
                                            getSentimentColor(word.sentiment_score) === 'red' ? '#db2828' : '#fbbd08'}`,
                        borderRadius: '20px',
                        fontSize: `${size}em`,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        display: 'inline-block'
                      }}
                      title={`${word.word} - ${word.frequency} mentions (${word.source})`}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      {word.word}
                    </div>
                  );
                })}
            </div>
            
            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <Header as="h5">Word Size = Popularity | Color = Sentiment</Header>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#e8f5e8', border: '2px solid #21ba45', borderRadius: '10px' }}></div>
                  <span>Positive</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#fff9e6', border: '2px solid #fbbd08', borderRadius: '10px' }}></div>
                  <span>Neutral</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#fce8e8', border: '2px solid #db2828', borderRadius: '10px' }}></div>
                  <span>Negative</span>
                </div>
              </div>
            </div>
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Markets',
      render: () => (
        <Tab.Pane>
          <DijonMarkets />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div id="gipirt">
      <DijonCard
        width={'100%'}
        height={'100vh'}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={'gipirt'}
        content={
          <div className={"container mx-auto px-4"} style={{
              height: '100%',
              overflow: 'auto',
              padding: '20px'
          }}>
            <Header as="h2" style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Icon name="chart line" />
              GIPIRT - Popularity & Sentiment Tracker
            </Header>
            
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Loader active inline='centered' size='large' />
                <p style={{ marginTop: '20px' }}>Loading trending data...</p>
              </div>
            )}
            
            {!loading && (
              <Tab 
                panes={panes} 
                activeIndex={activeIndex}
                onTabChange={(e, { activeIndex }) => setActiveIndex(activeIndex)}
              />
            )}
          </div>
        }
      />
    </div>
  );
}

export default DijonGipirt;